import { useRef, useState } from 'react';

import { withRedemptionToken } from '@utils/redemptionRoute';
import { ArrowLeft, ArrowRight, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { RedemptionLayout } from '../../redemption/components/RedemptionLayout';
import { useAuth } from '../context/AuthContext';

export const ForgotPassword = () => {
  const { resetPassword } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const pending = useRef(false);
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValidEmail || pending.current) return;

    pending.current = true;
    setIsLoading(true);
    try {
      await resetPassword(email);
      setIsSubmitted(true);
    } catch {
      // AuthProvider displays the error; keep the form available for another attempt.
    } finally {
      pending.current = false;
      setIsLoading(false);
    }
  };

  return (
    <RedemptionLayout step={0} showCodeNote={!!token}>
      <div className="redemption-purpose">
        <KeyRound size={16} aria-hidden="true" />
        {t('flow.eyebrow')}
      </div>
      <Link to={withRedemptionToken('/login', token)} className="redemption-back">
        <ArrowLeft size={15} aria-hidden="true" />
        {t('forgot_password_page.back_to_login')}
      </Link>
      <h1>{t('forgot_password_page.title')}</h1>
      {isSubmitted ? (
        <p className="redemption-body" role="status">
          {t('forgot_password_page.success_message')}
        </p>
      ) : (
        <>
          <p className="redemption-body">{t('forgot_password_page.description')}</p>
          <form onSubmit={handleSubmit} className="redemption-form">
            <div>
              <label htmlFor="reset-email">{t('forgot_password_page.email_label')}</label>
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                placeholder={t('forgot_password_page.email_placeholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={!isValidEmail || isLoading}
              aria-busy={isLoading}
              className="redemption-primary"
            >
              {isLoading ? t('forgot_password_page.sending') : t('forgot_password_page.submit')}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          </form>
        </>
      )}
    </RedemptionLayout>
  );
};
